import React, { useState, useEffect, useCallback } from "react";

const KnightTourGame = () => {
  // 骑士的8种可能移动方向 (行变化, 列变化)
  const knightMoves = [
    [-2, -1],
    [-2, 1],
    [-1, -2],
    [-1, 2],
    [1, -2],
    [1, 2],
    [2, -1],
    [2, 1],
  ];

  // 预设棋盘形状
  const presetBoards = {
    "3x4": {
      name: "3×4",
      pattern: Array(3)
        .fill()
        .map(() => Array(4).fill(true)),
    },
    "5x5": {
      name: "5×5",
      pattern: Array(5)
        .fill()
        .map(() => Array(5).fill(true)),
    },
    "8x8": {
      name: "8×8",
      pattern: Array(8)
        .fill()
        .map(() => Array(8).fill(true)),
    },
    cross: {
      name: "十字形",
      pattern: [
        [false, true, true, false],
        [true, true, true, true],
        [true, true, true, true],
        [false, true, true, false],
      ],
    },
  };

  const [boardPattern, setBoardPattern] = useState(presetBoards["3x4"].pattern);
  const [selectedPreset, setSelectedPreset] = useState("3x4");
  const [knightPosition, setKnightPosition] = useState(null);
  const [visitedCells, setVisitedCells] = useState(new Set());
  const [movePath, setMovePath] = useState([]);
  const [showValidMoves, setShowValidMoves] = useState(true);
  const [showPath, setShowPath] = useState(true);
  const [showLines, setShowLines] = useState(true);
  const [showMoveCount, setShowMoveCount] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // 添加窗口大小状态
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [cellSize, setCellSize] = useState(56); // 默认格子大小

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 计算动态格子大小
  useEffect(() => {
    const boardRows = boardPattern.length;
    const boardCols = boardPattern[0] ? boardPattern[0].length : 0;
    
    // 预留空间：左右padding(48px) + 卡片padding(48px) + 边距(48px) + 滚动条等(32px)
    const reservedWidth = 176;
    const availableWidth = windowWidth - reservedWidth;
    
    // 计算每个格子的最大尺寸
    const maxCellSize = Math.floor(availableWidth / boardCols);
    
    // 限制最小和最大尺寸
    const minCellSize = 32; // 最小32px
    const maxAllowedCellSize = 80; // 最大80px
    
    const newCellSize = Math.max(minCellSize, Math.min(maxCellSize, maxAllowedCellSize));
    setCellSize(newCellSize);
  }, [windowWidth, boardPattern]);

  // 获取有效的棋盘位置
  const getValidCells = useCallback(() => {
    const cells = [];
    for (let row = 0; row < boardPattern.length; row++) {
      for (let col = 0; col < boardPattern[row].length; col++) {
        if (boardPattern[row][col]) {
          cells.push({ row, col });
        }
      }
    }
    return cells;
  }, [boardPattern]);

  // 检查位置是否在棋盘内且有效
  const isValidPosition = useCallback(
    (row, col) => {
      return (
        row >= 0 &&
        row < boardPattern.length &&
        col >= 0 &&
        col < boardPattern[row].length &&
        boardPattern[row][col]
      );
    },
    [boardPattern]
  );

  // 获取从当前位置可以移动到的有效位置
  const getValidMoves = useCallback(
    (fromRow, fromCol) => {
      const validMoves = [];
      knightMoves.forEach(([rowOffset, colOffset]) => {
        const newRow = fromRow + rowOffset;
        const newCol = fromCol + colOffset;
        if (
          isValidPosition(newRow, newCol) &&
          !visitedCells.has(`${newRow}-${newCol}`)
        ) {
          validMoves.push({ row: newRow, col: newCol });
        }
      });
      return validMoves;
    },
    [isValidPosition, visitedCells, knightMoves]
  );

  // 获取从当前位置可以移动到的有效位置，包括每个位置的下一步数量
  const getValidMovesWithCount = useCallback(
    (fromRow, fromCol) => {
      const validMoves = [];
      knightMoves.forEach(([rowOffset, colOffset]) => {
        const newRow = fromRow + rowOffset;
        const newCol = fromCol + colOffset;
        if (
          isValidPosition(newRow, newCol) &&
          !visitedCells.has(`${newRow}-${newCol}`)
        ) {
          // 计算从这个位置出发还能走多少步
          const nextMoves = getValidMoves(newRow, newCol);
          validMoves.push({
            row: newRow,
            col: newCol,
            nextMovesCount: nextMoves.length,
          });
        }
      });
      return validMoves;
    },
    [isValidPosition, visitedCells, knightMoves, getValidMoves]
  );

  // 重置游戏
  const resetGame = useCallback(() => {
    setKnightPosition(null);
    setVisitedCells(new Set());
    setMovePath([]);
    setGameComplete(false);
    setGameOver(false);
    setGameStarted(false);
    setErrorMessage("请点击一个格子选择起始位置");
  }, []);

  // 切换棋盘预设
  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    setBoardPattern(presetBoards[preset].pattern);
    // 切换棋盘后自动重新开始
    setTimeout(() => resetGame(), 0);
  };

  // 获取棋盘格子的背景色 - 统一风格
  const getCellBackgroundClass = (
    isKnightPosition,
    isValidMove,
    isStartPosition,
    isVisited
  ) => {
    if (isKnightPosition) {
      return "bg-red-300";
    } else if (isValidMove && !gameComplete && !gameOver) {
      return "bg-green-200 hover:bg-green-300";
    } else if (isStartPosition) {
      return "bg-yellow-200 hover:bg-yellow-300";
    } else if (isVisited) {
      return "bg-blue-200";
    } else {
      return "bg-gray-100";
    }
  };

  // 处理点击棋盘格子
  const handleCellClick = (row, col) => {
    // 如果游戏还没开始，设置起始位置
    if (!gameStarted) {
      if (isValidPosition(row, col)) {
        const startPosition = { row, col };
        setKnightPosition(startPosition);
        setVisitedCells(new Set([`${row}-${col}`]));
        setMovePath([startPosition]);
        setGameStarted(true);
        setErrorMessage("");
      }
      return;
    }

    if (gameComplete || gameOver || !knightPosition) return;

    const currentValidMoves = getValidMoves(
      knightPosition.row,
      knightPosition.col
    );
    const isValidMove = currentValidMoves.some(
      (move) => move.row === row && move.col === col
    );

    if (isValidMove) {
      const newPosition = { row, col };
      const newVisited = new Set([...visitedCells, `${row}-${col}`]);
      const newPath = [...movePath, newPosition];

      setKnightPosition(newPosition);
      setVisitedCells(newVisited);
      setMovePath(newPath);
      setErrorMessage("");

      // 检查是否完成游戏
      const totalValidCells = getValidCells().length;
      if (newVisited.size === totalValidCells) {
        setGameComplete(true);
      } else {
        // 检查是否还有可移动的位置
        const nextValidMoves = getValidMoves(row, col);
        if (nextValidMoves.length === 0) {
          setGameOver(true);
        }
      }
    } else {
      setErrorMessage("无效移动！骑士只能按L形移动到未访问过的格子。");
      setTimeout(() => setErrorMessage(""), 2000);
    }
  };

  // 初始化游戏
  useEffect(() => {
    resetGame();
  }, [resetGame]);

  // 计算格子中心位置（基于当前格子大小）
  const getCellCenter = (row, col) => {
    return {
      x: col * cellSize + cellSize / 2,
      y: row * cellSize + cellSize / 2,
    };
  };

  // 生成路径线段
  const generatePathLines = () => {
    if (movePath.length < 2) return [];

    const lines = [];
    for (let i = 0; i < movePath.length - 1; i++) {
      const from = getCellCenter(movePath[i].row, movePath[i].col);
      const to = getCellCenter(movePath[i + 1].row, movePath[i + 1].col);
      lines.push({
        key: `line-${i}`,
        from,
        to,
        index: i,
      });
    }
    return lines;
  };

  const validCells = getValidCells();
  const currentValidMoves = knightPosition
    ? getValidMoves(knightPosition.row, knightPosition.col)
    : [];
  const currentValidMovesWithCount = knightPosition
    ? getValidMovesWithCount(knightPosition.row, knightPosition.col)
    : [];
  const totalCells = validCells.length;
  const visitedCount = visitedCells.size;
  const remainingCells = totalCells - visitedCount;
  const pathLines = generatePathLines();

  // 计算SVG视窗大小（基于当前格子大小）
  const boardRows = boardPattern.length;
  const boardCols = boardPattern[0] ? boardPattern[0].length : 0;
  const svgWidth = boardCols * cellSize;
  const svgHeight = boardRows * cellSize;

  // 动态计算文字大小
  const getFontSize = () => {
    if (cellSize >= 56) return 'text-lg';
    if (cellSize >= 40) return 'text-base';
    return 'text-sm';
  };

  const getKnightSize = () => {
    if (cellSize >= 56) return 'text-3xl';
    if (cellSize >= 40) return 'text-2xl';
    return 'text-xl';
  };

  const getBadgeSize = () => {
    if (cellSize >= 56) return 'w-6 h-6 text-xs';
    if (cellSize >= 40) return 'w-5 h-5 text-xs';
    return 'w-4 h-4 text-xs';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-5xl mx-auto">
        {/* 头部标题 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">
            骑士巡游问题
          </h1>
        </div>

        {/* 控制面板 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 mb-6">
          {/* 棋盘选择 */}
          <div className="flex flex-wrap items-center gap-6 mb-6">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                棋盘类型：
              </label>
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                {Object.entries(presetBoards).map(([key, board]) => (
                  <option key={key} value={key}>
                    {board.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={resetGame}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              重新开始
            </button>
          </div>

          {/* 显示选项 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <label className="flex items-center space-x-2 group cursor-pointer">
              <input
                type="checkbox"
                checked={showValidMoves}
                onChange={(e) => setShowValidMoves(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                显示可走方格
              </span>
            </label>
            <label className="flex items-center space-x-2 group cursor-pointer">
              <input
                type="checkbox"
                checked={showMoveCount}
                onChange={(e) => setShowMoveCount(e.target.checked)}
                disabled={!showValidMoves}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                显示下一步出口数
              </span>
            </label>
            <label className="flex items-center space-x-2 group cursor-pointer">
              <input
                type="checkbox"
                checked={showPath}
                onChange={(e) => setShowPath(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                记录行动序号
              </span>
            </label>
            <label className="flex items-center space-x-2 group cursor-pointer">
              <input
                type="checkbox"
                checked={showLines}
                onChange={(e) => setShowLines(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                显示移动路线
              </span>
            </label>
          </div>

          {/* 游戏状态 */}
          <div className="flex justify-between items-center">
            <div className="text-sm">
              {!gameStarted ? (
                <span className="text-blue-600 font-medium">
                  🎯 请选择起始位置
                </span>
              ) : (
                <span className="text-slate-600">
                  进度：
                  <span className="font-semibold text-slate-800">
                    {visitedCount}
                  </span>
                  <span className="text-slate-500">/{totalCells}</span>
                  {gameOver && !gameComplete && (
                    <span className="text-red-600 ml-2">
                      (剩余 {remainingCells} 格)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* 进度条 */}
          {gameStarted && (
            <div className="mt-3 w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 ease-out"
                style={{ width: `${(visitedCount / totalCells) * 100}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* 消息提示 */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {errorMessage}
          </div>
        )}

        {gameComplete && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl">
            🎉 恭喜！你完成了骑士巡游，访问了所有{totalCells}个格子！
          </div>
        )}

        {gameOver && !gameComplete && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 text-orange-700 rounded-xl">
            😔 游戏结束！骑士无法继续移动了。你访问了{visitedCount}个格子，还剩
            {remainingCells}个格子未访问。
          </div>
        )}

        {/* 棋盘 */}
        <div className="flex justify-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 p-6">
            <div className="inline-block relative">
              {/* 棋盘格子 */}
              {boardPattern.map((row, rowIndex) => (
                <div key={rowIndex} className="flex">
                  {row.map((isValid, colIndex) => {
                    const cellKey = `${rowIndex}-${colIndex}`;
                    const isKnightPosition =
                      knightPosition &&
                      knightPosition.row === rowIndex &&
                      knightPosition.col === colIndex;
                    const isVisited = visitedCells.has(cellKey);
                    const isValidMove =
                      gameStarted &&
                      showValidMoves &&
                      currentValidMoves.some(
                        (move) => move.row === rowIndex && move.col === colIndex
                      );
                    const validMoveWithCount = currentValidMovesWithCount.find(
                      (move) => move.row === rowIndex && move.col === colIndex
                    );
                    const isStartPosition = !gameStarted && isValid;

                    // 获取路径编号
                    const pathIndex = movePath.findIndex(
                      (pos) => pos.row === rowIndex && pos.col === colIndex
                    );

                    if (!isValid) {
                      return (
                        <div
                          key={colIndex}
                          style={{ width: cellSize, height: cellSize }}
                        ></div>
                      );
                    }

                    const backgroundClass = getCellBackgroundClass(
                      isKnightPosition,
                      isValidMove,
                      isStartPosition,
                      isVisited
                    );

                    let cellClass = `border border-slate-400 flex items-center justify-center font-bold transition-all duration-200 relative text-gray-800 ${backgroundClass} `;

                    if (isValidMove || isStartPosition) {
                      cellClass += " cursor-pointer ";
                    }

                    return (
                      <div
                        key={colIndex}
                        className={cellClass}
                        style={{ width: cellSize, height: cellSize }}
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                      >
                        {/* 显示内容 */}
                        <div className="flex items-center justify-center w-full h-full">
                          {isKnightPosition ? (
                            <span className={getKnightSize()}>♘</span>
                          ) : showPath && pathIndex >= 0 ? (
                            <span className={`${getFontSize()} font-bold leading-none`}>
                              {pathIndex + 1}
                            </span>
                          ) : showMoveCount && validMoveWithCount ? (
                            <span className={`inline-flex items-center justify-center ${getBadgeSize()} font-semibold text-white bg-purple-500 rounded-full shadow-sm`}>
                              {validMoveWithCount.nextMovesCount}
                            </span>
                          ) : isVisited ? (
                            <span className={getFontSize()}>·</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* SVG 路径连线覆盖层 */}
              {showLines && pathLines.length > 0 && (
                <svg
                  className="absolute top-0 left-0 pointer-events-none"
                  width={svgWidth}
                  height={svgHeight}
                  style={{ zIndex: 5 }}
                >
                  {pathLines.map((line) => (
                    <line
                      key={line.key}
                      x1={line.from.x}
                      y1={line.from.y}
                      x2={line.to.x}
                      y2={line.to.y}
                      stroke="#F97316"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  ))}
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* 棋盘尺寸提示 */}
        <div className="mt-4 text-center text-sm text-slate-500">
          棋盘格子大小: {cellSize}px × {cellSize}px (自动调整以适应屏幕宽度)
        </div>

        {/* 游戏说明 */}
        <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="font-bold text-slate-800 mb-3">游戏说明</h3>
          <div className="text-sm text-slate-600 space-y-2">
            <p>• 点击格子选择起始位置，骑士按L形移动</p>
            <p>• 目标：访问每个格子恰好一次</p>
            <p className="flex items-center">
              •{" "}
              <span className="inline-block w-4 h-4 bg-green-200 border border-slate-400 mr-2 ml-1"></span>
              绿色格子能走，
              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-purple-500 rounded-full shadow-sm ml-1 mr-1">
                3
              </span>
              代表出口数（可选路径数）
            </p>
            <p className="flex items-center">
              •{" "}
              <span className="inline-block w-4 h-4 bg-blue-200 border border-slate-400 mr-2 ml-1"></span>
              蓝色格子已走过，大数字显示步数序号
            </p>
            <p className="flex items-center">
              •{" "}
              <span className="inline-block w-8 h-0.5 bg-orange-500 mr-2 ml-1"></span>
              橙色线段显示移动路径
            </p>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default KnightTourGame;
